(ns app.front.view.group.admin.express-issuance-config
  "Admin config section for express issuance."
  (:require [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.verdict.model :as verdict-model]
            [app.domain.verdict.section :as verdict-section]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.admin.templates.subs :as template-subs]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(defn- resolve-verdict-defaults
  "Collect verdict defaults from the unsaved group-config atom and inherited ancestor configs.
   `unsaved-config` is the deref'd *group-config atom.
   `parent-config` is the resolved parent config from the editor context."
  [unsaved-config parent-config]
  (concat (:group.config/verdict-defaults unsaved-config)
          (:group.config/verdict-defaults parent-config)))

(defn- template-input-sections
  "Get input sections for a template from the entity-db."
  [template]
  (->> (eu/descendants template)
       (filter #(contains? verdict-section/input-types (:verdict.section/type %)))))

(defn- eligible-templates
  "Filter templates whose input sections are structurally eligible for express issuance."
  [templates]
  (->> templates
       (filter (fn [template]
                 (verdict-model/template-structurally-eligible-for-express-issuance?
                   (template-input-sections template))))))

(defn- defaults-covered?
  [template verdict-defaults]
  (verdict-model/template-express-issuance-defaults-covered?
    (template-input-sections template) verdict-defaults))

(defn- decorate-inherited-template-name [superdomain template]
  (if (= (-> template :domain/authority-group :db/id) (:db/id superdomain))
    (update (eu/->map template) :verdict.template/name str " (" (:group/name superdomain) ")")
    template))

(rf/reg-sub ::express-issuance-eligible-templates
  (fn [[_ permit-type group-id superdomain-id]]
    ;; Subscribe without name-kw so templates remain as Entity objects
    ;; (eu/->map conversion in update-template-names would break eu/descendants)
    [(rf/subscribe [::template-subs/templates
                    group-id
                    superdomain-id
                    {:entity-type  :entity.type/verdict-template
                     :permit-types #{permit-type}}
                    nil nil])
     (rf/subscribe [::gas/superdomain])])
  (fn [[templates superdomain] [_ _permit-type _group-id _superdomain-id]]
    (->> (eligible-templates templates)
         (map (partial decorate-inherited-template-name superdomain)))))

(defn express-issuance-config
  [{:keys [tr *group-config parent-config permit-type target group-id superdomain-id] :as editor-ctx}]
  (when (and (<sub [::ff/enabled? :feature-flag.id/express-issuance])
             (contains? ptc-model/permit-types-with-express-issuance permit-type))
    (let [verdict-defaults (resolve-verdict-defaults @*group-config parent-config)
          permit-templates (<sub [::express-issuance-eligible-templates permit-type group-id superdomain-id])
          sign-off-active? (or (:group.config/permit-sign-off? @*group-config)
                               (:group.config/permit-sign-off? parent-config))]
      [:div.md:flex
       (cc/form-group
         {:legend-text (tr [:group-admin/express-issuance])}
         (if sign-off-active?
           (cc/checkbox {:id         (str "express-issuance" target)
                         :label-text (tr [:group.config/express-issuance?])
                         :checked    false
                         :disabled   true})
           [acommon/checkbox
            (assoc editor-ctx :on-change
                   (fn [checked?]
                     (when-not checked?
                       ;; Set to nil to clear from config
                       (swap! *group-config assoc :group.config/express-issuance-template nil))))
            :group.config/express-issuance?])
         [ui/help (if sign-off-active?
                    (tr [:group-admin/express-issuance-incompatible-with-sign-off])
                    (tr [:group-admin/express-issuance-help]))]
         (when (and (not sign-off-active?)
                    (or (:group.config/express-issuance? @*group-config)
                        (:group.config/express-issuance? parent-config)))
           (let [parent-template (-> parent-config :group.config/express-issuance-template vals last)]
             [:<>
              (when parent-template
                [:div.my-4
                 (tr [:group-admin/express-issuance-parent-template]
                     [(:verdict.template/name parent-template)])])
              (let [selected-template-id (:group.config/express-issuance-template @*group-config)
                    effective-template   (if selected-template-id
                                           (<sub [::entities/entity selected-template-id])
                                           parent-template)]
                [:<>
                 [ui-select/select {:id           (str "express-issuance-template-" target)
                                    :class-name   "select-w-full"
                                    :label-text   (tr [:group-admin/template])
                                    :hide-label   false
                                    :required     (nil? parent-template)
                                    :allow-empty? (some? parent-template)
                                    :empty-text   (tr [:group-admin/inherit])
                                    :value        (:group.config/express-issuance-template @*group-config)
                                    :values       (map :db/id permit-templates)
                                    :val->str     #(-> permit-templates
                                                       (p/find-first {:db/id %})
                                                       :verdict.template/name)
                                    :on-change    #(swap! *group-config assoc :group.config/express-issuance-template %)}]
                 (when (and effective-template
                            (not (defaults-covered? effective-template verdict-defaults)))
                   [ui/help (tr [:group-admin/express-issuance-missing-verdict-defaults])])])])))])))


(defn valid? [group-config parent-config permit-type]
  (if (and (<sub [::ff/enabled? :feature-flag.id/express-issuance])
           (contains? ptc-model/permit-types-with-express-issuance permit-type))
    (let [express-issuance? (or (:group.config/express-issuance? group-config)
                                (:group.config/express-issuance? parent-config))
          template-id       (or (:group.config/express-issuance-template group-config)
                                (-> parent-config :group.config/express-issuance-template vals last :db/id))
          template          (when template-id
                              (<sub [::entities/entity template-id]))
          verdict-defaults  (resolve-verdict-defaults group-config parent-config)]
      (or (not express-issuance?)
          (and template
               (verdict-model/template-eligible-for-express-issuance?
                 (template-input-sections template) verdict-defaults))))
    true))
